import {Extension} from "@tiptap/core";
import type {Editor} from "@tiptap/core";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {NodeSelection, Selection, TextSelection} from "@tiptap/pm/state";
import {getPrintoutRegionKind} from "../blocks/headerFooter/printoutRegionOptions";

interface TopLevelSelectionBlock {
    from: number;
    node: ProseMirrorNode;
    to: number;
}

const getTopLevelSelectionBlock = (selection: Selection): TopLevelSelectionBlock | null => {
    if (selection instanceof NodeSelection && selection.$from.depth === 0) {
        return {from: selection.from, node: selection.node, to: selection.to};
    }

    const {$head} = selection;

    if ($head.depth < 1) return null;

    return {
        from: $head.before(1),
        node: $head.node(1),
        to: $head.after(1),
    };
};

const isEmptyParagraph = (node?: ProseMirrorNode | null): node is ProseMirrorNode => (
    !!node && node.type.name === "paragraph" && node.content.size === 0
);

/** Move from any nested selection to an editable body line beside its outermost block. */
export const escapeCurrentPrintoutBlockToParagraph = (editor: Editor): boolean => {
    if (!editor.isEditable) return false;

    const {state, view} = editor;
    const currentBlock = getTopLevelSelectionBlock(state.selection);
    const paragraphType = state.schema.nodes.paragraph;

    if (!currentBlock || !paragraphType) return false;

    const regionKind = getPrintoutRegionKind(currentBlock.node.type.name);
    const insertPosition = regionKind === "footer" ? currentBlock.from : currentBlock.to;
    const $insert = state.doc.resolve(insertPosition);
    const paragraphAfter = $insert.nodeAfter;
    const paragraphBefore = $insert.nodeBefore;

    // Palette insertion and the trailing-body invariant often already provide the escape line.
    let existingCursorPosition: number | null = null;

    if (regionKind === "footer" && isEmptyParagraph(paragraphBefore)) {
        existingCursorPosition = insertPosition - paragraphBefore.nodeSize + 1;
    } else if (isEmptyParagraph(paragraphAfter)) {
        existingCursorPosition = insertPosition + 1;
    }

    if (existingCursorPosition !== null) {
        view.dispatch(
            state.tr
                .setSelection(TextSelection.create(state.doc, existingCursorPosition))
                .scrollIntoView(),
        );
        return true;
    }

    if (!$insert.parent.canReplaceWith($insert.index(), $insert.index(), paragraphType)) return false;

    const transaction = state.tr.insert(insertPosition, paragraphType.create());

    view.dispatch(
        transaction
            .setSelection(TextSelection.create(transaction.doc, insertPosition + 1))
            .scrollIntoView(),
    );

    return true;
};

// Escape and Enter return a selected atom block to a text cursor.
export const PrintoutSelectionKeymap = Extension.create({
    name: "printoutSelectionKeymap",
    // Override HardBreak's Mod-Enter binding while leaving Shift-Enter unchanged.
    priority: 3001,

    addKeyboardShortcuts() {
        const getSelectedBlock = () => {
            const {selection} = this.editor.state;

            return selection instanceof NodeSelection && selection.node.isBlock ? selection : null;
        };

        return {
            "Mod-Enter": () => {
                escapeCurrentPrintoutBlockToParagraph(this.editor);
                return true;
            },

            Escape: () => {
                const selection = getSelectedBlock();

                if (!selection) return false;

                const {state, view} = this.editor;
                const selectionAfter = TextSelection.near(state.doc.resolve(selection.to), 1);

                view.dispatch(state.tr.setSelection(selectionAfter).scrollIntoView());

                return true;
            },

            Enter: () => {
                const selection = getSelectedBlock();

                if (!selection) return false;

                const {state, view} = this.editor;
                const paragraphType = state.schema.nodes.paragraph;

                if (!paragraphType) return false;

                const transaction = state.tr.insert(selection.to, paragraphType.create());

                view.dispatch(
                    transaction
                        .setSelection(TextSelection.create(transaction.doc, selection.to + 1))
                        .scrollIntoView(),
                );

                return true;
            },
        };
    },
});
